import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Radio, Play, Pause, SkipBack, SkipForward, Monitor, Award, Users,
  ArrowLeft, Music2, RotateCcw, ChevronUp, ChevronDown, Trophy, Coffee, RefreshCw, Zap,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import { visionAPI } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useVisionSocket, useStageAudioSync } from '../lib/visionSocket'
import { openMIDI, listInputs, listOutputs, attachInputListener, eventKey, loadMappings, loadInputId, loadOutputId, loadMidiActive, saveMidiActive, runAction, normalizeMapping, applyLeds, clearAllLeds } from '../lib/visionMidi'
import TeamMark, { parseFlag } from '@/components/TeamMark'
import KempenvisionNav from '@/components/KempenvisionNav'

const VIEWS = [
  { key: 'idle',     label: 'Idle',        icon: Coffee,   color: 'text-slate-400' },
  { key: 'song',     label: 'Song',        icon: Music2,   color: 'text-pink-500' },
  { key: 'ceremony', label: 'Ceremony',    icon: Award,    color: 'text-yellow-500' },
  { key: 'results',  label: 'Results',     icon: Trophy,   color: 'text-emerald-500' },
]

const PHASES = [
  { key: 'setup',          label: 'Setup' },
  { key: 'live_songs',     label: 'Live songs' },
  { key: 'pauze',          label: 'Pauze' },
  { key: 'jury_reveal',    label: 'Jury reveal' },
  { key: 'public_vote',    label: 'Publiek stemt' },
  { key: 'public_reveal',  label: 'Publiek reveal' },
  { key: 'finished',       label: 'Klaar' },
]

const POINT_STEPS = [1,2,3,4,5,6,7,8,10,12]



export default function KempenvisionControlPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin'
  const [data, setData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const load = async () => {
    try {
      const { data } = await visionAPI.getControlData()
      setData(data); setError(null)
    } catch (e) { setError(e.message) }
  }

  useEffect(() => {
    if (!isAdmin) return
    load()
    const iv = setInterval(load, 1500)
    return () => clearInterval(iv)
  }, [isAdmin])
  useVisionSocket(() => { if (isAdmin) load() })

  // ---------- MIDI live-mode ----------
  const [midiActive, setMidiActive] = useState(() => loadMidiActive())
  const [midiStatus, setMidiStatus] = useState('off') // off | connecting | ready | error
  const dataRef = useRef(data), psRef = useRef(null), pshRef = useRef(null)
  const outputRef = useRef(null), mappingsRef = useRef({})
  useEffect(() => { dataRef.current = data })
  const toggleMidi = () => { const next = !midiActive; setMidiActive(next); saveMidiActive(next) }

  useEffect(() => {
    if (!midiActive) { setMidiStatus('off'); return }
    let detach = null, cancelled = false
    ;(async () => {
      setMidiStatus('connecting')
      try {
        const access = await openMIDI()
        if (cancelled) return
        const ins = listInputs(access)
        const outs = listOutputs(access)
        const inId  = loadInputId();  const outId = loadOutputId()
        const input  = ins.find(i => i.id === inId)  ?? ins[0]
        const output = outs.find(o => o.id === outId) ?? outs[0] ?? null
        if (!input) { setMidiStatus('error'); return }
        outputRef.current = output
        mappingsRef.current = loadMappings()
        detach = attachInputListener(input, (evt) => {
          const key = eventKey(evt); if (!key) return
          const m = loadMappings(); mappingsRef.current = m
          const spec = normalizeMapping(m[key])
          if (!spec.action) return
          const value = (evt.type === 0xB0) ? evt.data2 : undefined
          runAction(spec.action, { data: dataRef.current, patchState: psRef.current, patchShow: pshRef.current }, value)
        })
        // initial LED render
        if (output && dataRef.current) {
          applyLeds(output, mappingsRef.current, { state: dataRef.current.state, show: dataRef.current.show, entries: dataRef.current.entries })
        }
        setMidiStatus('ready')
      } catch (e) { console.warn('MIDI open failed', e); setMidiStatus('error') }
    })()
    return () => {
      cancelled = true
      if (detach) detach()
      if (outputRef.current) clearAllLeds(outputRef.current)
      outputRef.current = null
    }
  }, [midiActive])

  // Re-apply LEDs bij view/phase change
  useEffect(() => {
    if (!midiActive || !outputRef.current || !dataRef.current) return
    applyLeds(outputRef.current, mappingsRef.current, { state: dataRef.current.state, show: dataRef.current.show, entries: dataRef.current.entries })
  }, [
    midiActive,
    data?.state?.current_view,
    data?.state?.current_entry_id,
    data?.state?.ceremony_step,
    data?.state?.public_reveal_step,
    data?.state?.audio_playing,
    data?.state?.genre_override,
    data?.show?.phase,
    data?.show?.submissions_open,
  ])


  if (!isAdmin) return <div className="p-6 text-muted-foreground">Enkel voor admins.</div>
  if (!data) return <div className="p-6 text-muted-foreground">Laden…</div>
  if (!data.show) return (
    <div className="max-w-2xl mx-auto">
      <KempenvisionNav />
      <Card><CardContent className="p-6 text-center text-muted-foreground">
        Geen actieve show. <Link to="/kempenvision/admin" className="underline">Maak of activeer er één</Link>.
      </CardContent></Card>
    </div>
  )

  const { show, state, entries, juries, jury_progress, vote_stats, standings, current_jury_votes } = data
  const currentEntry = entries.find(e => e.id === state.current_entry_id)
  const currentJury  = juries.find(j => j.id === state.current_jury_id)

  const patchState = async (updates) => {
    setSaving(true); setError(null)
    try { await visionAPI.updateState(updates); await load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setSaving(false) }
  }
  const patchShow = async (updates) => {
    setSaving(true); setError(null)
    try { await visionAPI.updateShow(show.id, updates); await load() }
    catch (e) { setError(e.response?.data?.error ?? e.message) }
    finally { setSaving(false) }
  }

  psRef.current  = patchState
  pshRef.current = patchShow

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <KempenvisionNav />
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[4px] border border-border bg-card flex items-center justify-center" style={{ borderLeft: "2px solid rgb(var(--acid-rgb))" }}>
            <Radio className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Control</div>
            <h1 className="text-xl font-bold">{show.name}{show.edition ? ` — ${show.edition}` : ''}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={midiActive ? 'default' : 'outline'}
            size="sm"
            className={midiActive ? (midiStatus === 'ready' ? 'bg-cyan-500 hover:bg-cyan-600' : 'bg-yellow-500 hover:bg-yellow-600') : ''}
            onClick={toggleMidi}
            title={midiStatus === 'ready' ? 'MIDI actief' : midiStatus === 'connecting' ? 'MIDI verbinden…' : midiStatus === 'error' ? 'MIDI fout — check /kempenvision/midi' : 'MIDI uit'}>
            <Zap className="h-4 w-4 mr-1" />MIDI {midiStatus === 'ready' ? '●' : midiStatus === 'connecting' ? '…' : midiStatus === 'error' ? '✕' : ''}
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href="/kempenvision/stage" target="_blank" rel="noopener"><Monitor className="h-4 w-4 mr-1" />Stage</a>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => {
            if (window.history.length > 1) navigate(-1); else navigate('/')
          }} title="Vorige pagina (browser-history)">
            <ArrowLeft className="h-4 w-4 mr-1" />Vorige
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/kempenvision"><ArrowLeft className="h-4 w-4 mr-1" />Kempenvision</Link>
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-3 text-sm text-red-500">⚠ {error}</CardContent>
        </Card>
      )}

      {/* View mode toggler */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Stage weergave</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {VIEWS.map(v => {
            const Icon = v.icon
            const active = state.current_view === v.key
            return (
              <Button key={v.key} variant={active ? 'default' : 'outline'}
                      className={`h-16 ${active ? '' : v.color}`}
                      onClick={() => patchState({ current_view: v.key })} disabled={saving}>
                <div className="flex flex-col items-center gap-1">
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">{v.label}</span>
                </div>
              </Button>
            )
          })}
        </CardContent>
      </Card>

      {/* Song controls */}
      {state.current_view === 'song' && (
        <SongControls
          entries={entries} currentEntry={currentEntry} state={state}
          saving={saving} patchState={patchState}
        />
      )}

      {/* Ceremony controls */}
      {state.current_view === 'ceremony' && (
        <CeremonyControls
          juries={juries} currentJury={currentJury} state={state}
          current_jury_votes={current_jury_votes}
          saving={saving} patchState={patchState}
        />
      )}

      {/* Publiek-flow navigator — na jury-reveal wil de regisseur snel naar
          publiek-stemmen en dan naar publiek-reveal. Grote knop met de
          volgende logische stap; de complete phase-picker blijft beschikbaar
          in de Setup-tab voor uitzonderingen. */}
      {show.phase === 'jury_reveal' && (
        <Button size="lg" className="w-full h-16 bg-cyan-500 hover:bg-cyan-600 text-white text-base font-bold uppercase tracking-widest"
                disabled={saving}
                onClick={() => patchShow({ phase: 'public_vote', vote_open: true })}>
          → Open publiek-stemmen
        </Button>
      )}
      {show.phase === 'public_vote' && (
        <Button size="lg" className="w-full h-16 bg-cyan-500 hover:bg-cyan-600 text-white text-base font-bold uppercase tracking-widest"
                disabled={saving}
                onClick={() => patchShow({ phase: 'public_reveal', vote_open: false })}>
          → Start publiek reveal
        </Button>
      )}

      {show.phase === 'public_reveal' && (
        <PublicRevealControls
          state={state}
          entries={entries}
          publicRevealOrder={data.public_reveal_order || []}
          currentReveal={data.current_public_reveal}
          saving={saving}
          patchState={patchState}
        />
      )}

      {/* Winner-audio: alleen in results-view én er is een winnaar met audio.
          Speelt via dezelfde `audio_playing`-flag als song-view, dus de stage-
          renderer heeft geen extra state nodig. Manuele play, geen autoplay. */}
      {state.current_view === 'results' && standings.length > 0 && (() => {
        const winner = entries.find(e => e.id === standings[0].entry_id)
        if (!winner?.audio_url) {
          return (
            <Card className="border-yellow-500/40 bg-yellow-500/5">
              <CardContent className="p-3 text-sm text-yellow-500">
                🎵 Winnaar heeft geen audiobestand; play-knop niet beschikbaar.
              </CardContent>
            </Card>
          )
        }
        return (
          <Card className="border-yellow-500/40 bg-yellow-500/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                Winnaar-nummer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-300">{winner.title}</div>
                <div className="text-sm text-muted-foreground">{standings[0].team_name}</div>
              </div>
              {state.audio_playing ? (
                <Button size="lg" className="w-full h-14 bg-red-500 hover:bg-red-600"
                        disabled={saving}
                        onClick={() => patchState({ audio_playing: false, current_entry_id: winner.id })}>
                  <Pause className="h-6 w-6 mr-2" /> Pauzeer winnaar-nummer
                </Button>
              ) : (
                <Button size="lg" className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-black"
                        disabled={saving}
                        onClick={() => patchState({ audio_playing: true, current_entry_id: winner.id })}>
                  <Play className="h-6 w-6 mr-2" /> Speel winnaar-nummer
                </Button>
              )}
            </CardContent>
          </Card>
        )
      })()}

      {/* Live info grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4" />Jury voortgang</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {jury_progress.length === 0 && <p className="text-xs text-muted-foreground">Nog geen jury's aangemaakt</p>}
            {jury_progress.map(j => (
              <div key={j.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate">{j.name}</span>
                <span className="text-xs text-muted-foreground font-mono">{j.votes_count}/10</span>
                {j.is_locked ? <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border" variant="outline">✓ ingediend</Badge>
                             : <Badge variant="outline" className="text-yellow-500 border-yellow-500/40">open</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4" />Publiek stem</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{vote_stats?.voter_count ?? 0}</div>
            <div className="text-xs text-muted-foreground">stemmers · {vote_stats?.total_votes ?? 0} punten totaal</div>
            <div className="text-xs text-muted-foreground mt-2">
              Publiek stem is {show.vote_open && show.public_scoring_mode === 'vote'
                ? <span className="text-emerald-400">OPEN</span>
                : <span className="text-muted-foreground">dicht</span>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Trophy className="h-4 w-4" />Stand top 5</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {standings.slice(0, 5).map((s, i) => (
              <div key={s.entry_id} className="flex items-center gap-2 text-sm">
                <span className="w-5 text-center font-mono text-xs text-muted-foreground">{i+1}</span>
                <TeamMark flag_colors={s.team_flag_colors} logo_url={s.team_logo_url} size="sm" />
                <span className="flex-1 truncate">{s.team_name}</span>
                <span className="font-mono font-bold tabular-nums">{s.total_points}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

/* ============================================================
   SONG CONTROLS
============================================================ */
function SongControls({ entries, currentEntry, state, saving, patchState }) {
  const idx = entries.findIndex(e => e.id === state.current_entry_id)
  const prev = idx > 0 ? entries[idx - 1] : null
  const next = idx >= 0 && idx < entries.length - 1 ? entries[idx + 1] : null
  const [audio, setAudio] = useState({ position: 0, duration: 0 })
  const { emitSeek } = useStageAudioSync({ onTick: (data) => setAudio(data) })

  const goto = (entry, autoPlay = false) => {
    patchState({
      current_entry_id: entry?.id ?? null,
      genre_override: null,
      audio_playing: autoPlay,
      audio_position: 0,
    })
  }

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Music2 className="h-4 w-4" />Song controls</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Actieve entry</div>
          <Select
            value={state.current_entry_id ? String(state.current_entry_id) : ''}
            onValueChange={(v) => goto(entries.find(e => e.id === Number(v)))}
            disabled={saving || entries.length === 0}
          >
            <SelectTrigger><SelectValue placeholder="— kies entry —" /></SelectTrigger>
            <SelectContent>
              {entries.map(e => (
                <SelectItem key={e.id} value={String(e.id)}>
                  #{e.running_order} · {e.team_name} — {e.title} ({e.genre})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentEntry && (
          <div className="p-3 rounded-lg border border-border bg-card flex items-center gap-3">
            <TeamMark flag_colors={currentEntry.team_flag_colors} logo_url={currentEntry.team_logo_url} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{currentEntry.title}</div>
              <div className="text-xs text-muted-foreground truncate">
                {currentEntry.team_name} · <span className="uppercase">{currentEntry.genre}</span>
                {currentEntry.bpm ? ` · ${currentEntry.bpm} BPM` : ''}
              </div>
              {!currentEntry.audio_url && <div className="text-xs text-yellow-500 mt-1">⚠ Geen audio geüpload</div>}
            </div>
          </div>
        )}

        {/* Progress bar (klikbaar) */}
        {currentEntry?.audio_url && (
          <ProgressBar position={audio.position} duration={audio.duration}
                       onSeek={(time) => emitSeek({ time })} />
        )}

        {/* Transport */}
        <div className="flex gap-2">
          <Button variant="outline" size="lg" disabled={!prev || saving} onClick={() => goto(prev)}>
            <SkipBack className="h-5 w-5 mr-1" />Vorige
          </Button>
          {state.audio_playing ? (
            <Button size="lg" className="flex-1 h-14" disabled={saving || !currentEntry?.audio_url}
                    onClick={() => patchState({ audio_playing: false })}>
              <Pause className="h-6 w-6 mr-2" />PAUZE
            </Button>
          ) : (
            <Button size="lg" className="flex-1 h-14 bg-pink-500 hover:bg-pink-600" disabled={saving || !currentEntry?.audio_url}
                    onClick={() => patchState({ audio_playing: true })}>
              <Play className="h-6 w-6 mr-2" />PLAY
            </Button>
          )}
          <Button variant="outline" size="lg" disabled={!next || saving} onClick={() => goto(next)}>
            Volgende<SkipForward className="h-5 w-5 ml-1" />
          </Button>
        </div>

        {/* Genre override */}
        <div>
          <div className="text-xs text-muted-foreground mb-1">Genre override (visualizer)</div>
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" variant={!state.genre_override ? 'default' : 'outline'}
                    onClick={() => patchState({ genre_override: null })} disabled={saving}>
              Uit entry
            </Button>
            {['edm','rock','pop','ballad','hiphop','synthwave'].map(g => (
              <Button key={g} size="sm" variant={state.genre_override === g ? 'default' : 'outline'}
                      onClick={() => patchState({ genre_override: g })} disabled={saving}>
                {g}
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============================================================
   CEREMONY CONTROLS
============================================================ */
function CeremonyControls({ juries, currentJury, state, current_jury_votes, saving, patchState }) {
  const step = state.ceremony_step ?? 0
  const totalSteps = current_jury_votes?.length ?? 0
  const nextVote = totalSteps > 0 && step < totalSteps ? current_jury_votes[step] : null
  const currentVote = totalSteps > 0 && step > 0 ? current_jury_votes[step - 1] : null

  const setJury = (juryId) => patchState({ current_jury_id: Number(juryId), ceremony_step: 0 })
  const next = () => patchState({ ceremony_step: Math.min(step + 1, totalSteps) })
  const prev = () => patchState({ ceremony_step: Math.max(step - 1, 0) })
  const reset = () => patchState({ ceremony_step: 0 })

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4" />Ceremony controls</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Actieve jury (spokesperson)</div>
          <Select value={state.current_jury_id ? String(state.current_jury_id) : ''} onValueChange={setJury} disabled={saving || juries.length === 0}>
            <SelectTrigger><SelectValue placeholder="— kies jury —" /></SelectTrigger>
            <SelectContent>
              {juries.map(j => (
                <SelectItem key={j.id} value={String(j.id)}>
                  #{j.display_order} · {j.name}{j.spokesperson ? ` (${j.spokesperson})` : ''}{j.is_locked ? ' ✓' : ' (open)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {currentJury && !currentJury.is_locked && (
          <div className="p-3 rounded border border-yellow-500/40 bg-yellow-500/5 text-sm text-yellow-500">
            ⚠ Deze jury heeft haar stemmen nog niet definitief ingediend
          </div>
        )}

        {currentJury && totalSteps === 0 && currentJury.is_locked && (
          <div className="p-3 rounded border border-yellow-500/40 bg-yellow-500/5 text-sm text-yellow-500">
            Deze jury is gelockt maar heeft geen stemmen (mogelijk lock zonder verzenden)
          </div>
        )}

        {/* Step display */}
        <div className="grid grid-cols-3 gap-3 items-center">
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Getoond</div>
            <div className="text-2xl font-bold text-cyan-400 tabular-nums">
              {currentVote ? `${currentVote.points}` : '—'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {currentVote?.team_name || 'nog niks'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums">{step}/{totalSteps}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Stap</div>
          </div>
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Volgende</div>
            <div className="text-2xl font-bold text-pink-500 tabular-nums">
              {nextVote ? `${nextVote.points}` : '—'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {nextVote?.team_name || 'klaar'}
            </div>
          </div>
        </div>

        {/* Transport */}
        <div className="flex gap-2">
          <Button variant="outline" size="lg" onClick={reset} disabled={saving || step === 0}>
            <RotateCcw className="h-5 w-5 mr-1" />Reset
          </Button>
          <Button variant="outline" size="lg" onClick={prev} disabled={saving || step === 0}>
            <ChevronDown className="h-5 w-5" />
          </Button>
          <Button size="lg" className="flex-1 h-14 bg-yellow-500 text-black hover:bg-yellow-600"
                  onClick={next} disabled={saving || !nextVote}>
            <ChevronUp className="h-6 w-6 mr-2" />
            {nextVote ? `Reveal ${nextVote.points} pts → ${nextVote.team_name}` : 'Klaar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/* ============ Progress bar met click-to-seek ============ */
function ProgressBar({ position, duration, onSeek }) {
  const pct = duration > 0 ? Math.min(100, (position / duration) * 100) : 0
  const handleClick = (e) => {
    if (!duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    onSeek(Math.max(0, Math.min(1, x)) * duration)
  }
  const fmt = (t) => {
    if (!Number.isFinite(t) || t <= 0) return '0:00'
    const m = Math.floor(t / 60), sec = Math.floor(t % 60)
    return `${m}:${String(sec).padStart(2, '0')}`
  }
  return (
    <div className="space-y-1">
      <div className="h-3 rounded-full bg-muted overflow-hidden cursor-pointer relative group"
           onClick={handleClick}>
        <div className="h-full bg-pink-500 transition-[width] duration-200 ease-linear" style={{ width: `${pct}%` }} />
        {/* Hover indicator */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-30 bg-pink-500 pointer-events-none" />
      </div>
      <div className="flex justify-between text-xs font-mono tabular-nums text-muted-foreground">
        <span>{fmt(position)}</span>
        <span>{fmt(duration)}</span>
      </div>
    </div>
  )
}

/* ============ Public reveal controls ============ */
function PublicRevealControls({ state, entries, publicRevealOrder, currentReveal, saving, patchState }) {
  const step = state.public_reveal_step ?? 0
  const total = publicRevealOrder?.length ?? entries.length
  const nextId = publicRevealOrder?.[step]
  const nextEntry = nextId ? entries.find(e => e.id === nextId) : null

  const next = () => patchState({ public_reveal_step: Math.min(step + 1, total) })
  const prev = () => patchState({ public_reveal_step: Math.max(step - 1, 0) })
  const reset = () => patchState({ public_reveal_step: 0 })

  return (
    <Card className="border-cyan-500/40 bg-cyan-500/5">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2">
        <Users className="h-4 w-4 text-cyan-400" /> Publiek reveal
      </CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3 items-center">
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Getoond</div>
            <div className="text-2xl font-bold text-cyan-400 tabular-nums">
              {currentReveal ? currentReveal.points : '—'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {currentReveal?.team_name || 'nog niks'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums">{step}/{total}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Onthuld</div>
          </div>
          <div className="text-center p-3 rounded border border-border bg-card">
            <div className="text-xs text-muted-foreground uppercase">Volgende</div>
            <div className="text-2xl font-bold text-pink-500 tabular-nums">?</div>
            <div className="text-xs text-muted-foreground truncate">
              {nextEntry ? nextEntry.team_name : (step >= total ? 'klaar' : '—')}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="lg" onClick={reset} disabled={saving || step === 0}>
            <RotateCcw className="h-5 w-5 mr-1" />Reset
          </Button>
          <Button variant="outline" size="lg" onClick={prev} disabled={saving || step === 0}>
            <ChevronDown className="h-5 w-5" />
          </Button>
          <Button size="lg" className="flex-1 h-14 bg-cyan-500 text-black hover:bg-cyan-600"
                  onClick={next} disabled={saving || step >= total}>
            <ChevronUp className="h-6 w-6 mr-2" />
            {step >= total ? 'Alle publiek onthuld' : `Reveal → ${nextEntry?.team_name || ''}`}
          </Button>
        </div>
        <div className="text-xs text-muted-foreground text-center">
          Volgorde = laagste publiek-score eerst (klassieke Eurovision volgorde)
        </div>
      </CardContent>
    </Card>
  )
}


/* Hold-to-play knop — 1 sec ingedrukt houden triggert de actie. Voorkomt
   dat een per-ongeluk-klik de winnaar-audio start tijdens de show. Progress-
   balk vult tijdens het vasthouden; loslaten vóór voltooiing = niks. */
function HoldToPlayButton({ onFire, disabled, label = 'Houd vast om te starten', duration = 1000 }) {
  const [progress, setProgress] = useState(0)
  const startedRef = useRef(0)
  const rafRef = useRef(null)
  const cancel = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    startedRef.current = 0
    setProgress(0)
  }
  const tick = () => {
    const p = Math.min(1, (Date.now() - startedRef.current) / duration)
    setProgress(p)
    if (p >= 1) {
      cancel()
      onFire?.()
    } else {
      rafRef.current = requestAnimationFrame(tick)
    }
  }
  const start = (e) => {
    if (disabled || startedRef.current) return
    e.preventDefault?.()
    startedRef.current = Date.now()
    rafRef.current = requestAnimationFrame(tick)
  }
  useEffect(() => () => cancel(), [])
  return (
    <button type="button" disabled={disabled}
            onPointerDown={start}
            onPointerUp={cancel}
            onPointerLeave={cancel}
            onPointerCancel={cancel}
            className="relative w-full h-14 rounded-md border-2 border-yellow-500 bg-yellow-500/10 text-yellow-200 font-bold uppercase tracking-widest text-sm active:bg-yellow-500/20 disabled:opacity-30 disabled:cursor-not-allowed overflow-hidden select-none">
      <div className="absolute inset-y-0 left-0 bg-yellow-500/40 transition-none"
           style={{ width: `${progress * 100}%` }} />
      <div className="relative flex items-center justify-center gap-2">
        <Play className="h-5 w-5" />
        <span>{progress > 0 ? `Doorgaan… ${Math.round(progress * 100)}%` : label}</span>
      </div>
    </button>
  )
}
